import { Button, Space, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import type { TFunction } from 'i18next'
import type { MouseEvent } from 'react'
import type { ProductSummaryRoot } from '@repo/sdk'
import { StatusBadge } from '../../components/StatusBadge'
import { ThumbnailImage } from '../../components/ThumbnailImage'
import { formatKrw } from '@repo/shared'
import type { ProductStatusConfigMap, SalesChannelConfigMap } from './productStatusConfig'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

type ProductStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'SOLD_OUT'
type SalesChannel = 'BROADCAST' | 'MARKETPLACE'
const stop = (e: MouseEvent) => e.stopPropagation()

export interface ProductListColumnProps {
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onHide: (id: string) => void
  onUnhide: (id: string) => void
  t: TFunction
  statusConfig: ProductStatusConfigMap
  salesChannelConfig: SalesChannelConfigMap
}

export function createProductListColumns({
  onEdit,
  onDelete,
  onHide,
  onUnhide,
  t,
  statusConfig,
  salesChannelConfig,
}: ProductListColumnProps): ColumnType<ProductSummaryRoot>[] {
  return [
    {
      title: t('common.columns.thumbnail'),
      dataIndex: 'thumbnailImage',
      key: 'thumbnailImage',
      width: 64,
      render: (v: string) => <ThumbnailImage src={v} />,
    },
    {
      title: t('common.columns.productName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (v: string) => <Typography.Text ellipsis={{ tooltip: true }}>{v}</Typography.Text>,
    },
    {
      title: t('common.columns.category'),
      dataIndex: 'categories',
      key: 'categories',
      width: 150,
      render: (_: unknown, record: ProductSummaryRoot) => (
        <Space size={[0, 4]} wrap>
          {record.categories.map((c) => (
            <Tag key={c.id}>{c.name}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t('common.columns.price'),
      key: 'price',
      width: 100,
      align: 'right' as const,
      render: (_: unknown, r: ProductSummaryRoot) => formatKrw(r.price.amount),
    },
    {
      title: t('common.columns.stockQuantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right' as const,
    },
    {
      title: t('common.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: ProductStatus, r: ProductSummaryRoot) => (
        <Space size={[0, 4]} wrap>
          <StatusBadge status={v} configMap={statusConfig} />
          {r.hiddenAt && <Tag color="default">{t('common.status.hidden')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('common.columns.salesChannel'),
      dataIndex: 'salesChannel',
      key: 'salesChannel',
      width: 110,
      render: (v: SalesChannel) => <StatusBadge status={v} configMap={salesChannelConfig} />,
    },
    {
      title: t('common.columns.createdDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => (v ? formatKstDateTime(v, 'YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: t('common.columns.actions'),
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: unknown, r: ProductSummaryRoot) => (
        <Space size={0}>
          {r.salesChannel === 'MARKETPLACE' && (
            <Tooltip title={t('common.buttons.edit')}>
              <Button
                data-testid={`product-list-edit-btn-${r.id}`}
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  stop(e)
                  onEdit(r.id)
                }}
              />
            </Tooltip>
          )}
          {r.salesChannel === 'MARKETPLACE' &&
            (r.hiddenAt ? (
              <Tooltip title={t('common.buttons.unhide')}>
                <Button
                  data-testid={`product-list-unhide-btn-${r.id}`}
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={(e) => {
                    stop(e)
                    onUnhide(r.id)
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title={t('common.buttons.hide')}>
                <Button
                  data-testid={`product-list-hide-btn-${r.id}`}
                  type="text"
                  size="small"
                  icon={<EyeInvisibleOutlined />}
                  onClick={(e) => {
                    stop(e)
                    onHide(r.id)
                  }}
                />
              </Tooltip>
            ))}
          {r.salesChannel === 'MARKETPLACE' && (
            <Tooltip title={t('common.buttons.delete')}>
              <Button
                data-testid={`product-list-delete-btn-${r.id}`}
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  stop(e)
                  onDelete(r.id)
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]
}
